<script setup>
  import { ref, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { BSpinner } from 'bootstrap-vue-next';
  import { getUserAccesses, deleteUser } from '../services/UsersService';

  // FBKS-3987: read-only list of everything a user has access to (staff only).
  const { t } = useI18n();
  const i18nScope = ref('components.users_management.accesses');
  const emits = defineEmits(['accountDeleted']);

  const props = defineProps({
    user: {
      type: Object,
      required: true
    },
    // FBKS-4003: whole-account deletion lives here, staff-only.
    canDeleteAccount: {
      type: Boolean,
      default: false
    }
  });

  const loading = ref(true);
  const error = ref(false);
  const accesses = ref({ role: null, organisations: [] });
  const confirmingDelete = ref(false);
  const deleting = ref(false);

  const deleteAccountNow = async () => {
    deleting.value = true;
    try {
      await deleteUser(props.user.id);
      emits('accountDeleted', { type: 'success', message: 'delete_user_success' });
    } catch (e) {
      emits('accountDeleted', { type: 'error', message: 'delete_user_error' });
    } finally {
      deleting.value = false;
    }
  };

  onMounted(async () => {
    try {
      const response = await getUserAccesses(props.user.id);
      accesses.value = response.data;
    } catch (e) {
      error.value = true;
    } finally {
      loading.value = false;
    }
  });
</script>

<template>
  <div class="d-flex justify-content-center" v-if="loading">
    <BSpinner variant="secondary" />
  </div>

  <p class="text-danger" v-else-if="error">{{ t(`${i18nScope}.error`) }}</p>

  <template v-else>
    <p v-if="accesses.role">{{ t(`${i18nScope}.native_role`, { role: accesses.role }) }}</p>

    <p v-if="accesses.organisations.length === 0">{{ t(`${i18nScope}.no_organisations`) }}</p>

    <table class="table" v-else>
      <thead>
        <tr>
          <th scope="col">{{ t(`${i18nScope}.organisation`) }}</th>
          <th scope="col">{{ t(`${i18nScope}.type`) }}</th>
          <th scope="col">{{ t(`${i18nScope}.role`) }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="organisation in accesses.organisations" :key="`${organisation.type}-${organisation.id}`">
          <td>{{ organisation.name }}</td>
          <td>{{ organisation.type }}</td>
          <td>{{ organisation.role }}</td>
        </tr>
      </tbody>
    </table>

    <template v-if="props.canDeleteAccount">
      <hr>
      <button type="button" class="btn btn-danger" v-if="!confirmingDelete" @click="confirmingDelete = true">
        <i class="bi bi-trash"></i>
        {{ t(`${i18nScope}.delete_account`) }}
      </button>

      <div v-else>
        <p class="text-danger">{{ t(`${i18nScope}.delete_account_warning`) }}</p>
        <div class="d-flex justify-content-center" v-if="deleting">
          <BSpinner variant="secondary" />
        </div>
        <div class="d-flex gap-2" v-else>
          <button type="button" class="btn btn-danger" @click="deleteAccountNow">
            {{ t(`${i18nScope}.delete_account`) }}
          </button>
          <button type="button" class="btn btn-outline-secondary" @click="confirmingDelete = false">
            {{ t('components.users_management.user_details.cancel') }}
          </button>
        </div>
      </div>
    </template>
  </template>
</template>
